import { loadConfig, z } from '@cortex/config';

/** The web app's whole configuration, read once from the Vite build env. */
export const webConfigSchema = z.object({
  /** Where the API lives; "/" means same origin (the dev server proxies /api). */
  VITE_API_BASE_URL: z.string().default('/'),
  /** Where the accounts service lives; "/" means same origin (the dev server proxies /accounts). */
  VITE_ACCOUNTS_BASE_URL: z.string().default('/'),
  /** Where the assistant service lives (the chat panel); the dev server proxies /assistant. */
  VITE_ASSISTANT_BASE_URL: z.string().default('/assistant'),
  /**
   * The public origin of the assistant's per-project MCP (`<base>/mcp/p/<project>`), shown on
   * Settings and Projects. Empty means this UI's own origin (the dev server proxies /mcp).
   */
  VITE_MCP_BASE_URL: z.string().default(''),
  /**
   * The origin of the shared app runtime (ADR 0018, prototype mode): the Prototype page frames the
   * preview from it and signs the viewer in there (`POST /__session`). It must be on the same site as
   * this UI (the runtime's session cookie is SameSite=Lax), and the web servers' CSP frames and
   * connects to it (CORTEX_WEB_PROTOTYPE_RUNTIME / WEB_PROTOTYPE_RUNTIME_ORIGIN). `/`: this UI's own
   * origin (a project's standalone shell, ADR 0021, where one gateway serves both). Empty (the default):
   * no app runtime, unless the web server writes one into the page.
   */
  VITE_PROTOTYPE_RUNTIME_URL: z
    .string()
    .refine(
      (v) => v === '' || v === '/' || /^https?:\/\/[^/]/.test(v),
      'an http(s) URL, or / for this origin',
    )
    // Unset (and no meta from the server): this deployment has no app runtime, and the shell never calls one.
    .default(''),
  /** Build version shown on the login page; set by the image build. */
  VITE_CORTEX_VERSION: z.string().default('dev'),
});

export type WebConfig = z.infer<typeof webConfigSchema>;

export const config: WebConfig = loadConfig(webConfigSchema, import.meta.env as Record<string, string>);
